import { Accordion as AccordionPrimitive } from "@base-ui/react/accordion";
import { ChevronDownIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

function Accordion({ className, ...props }: AccordionPrimitive.Root.Props) {
  return <AccordionPrimitive.Root data-slot="accordion" className={cn("flex w-full flex-col", className)} {...props} />;
}

function AccordionItem({ className, ...props }: AccordionPrimitive.Item.Props) {
  return (
    <AccordionPrimitive.Item data-slot="accordion-item" className={cn("not-last:border-b", className)} {...props} />
  );
}

/**
 * Accordion trigger row. The chevron sits on the left of the title; an optional
 * `actions` node renders on the right as a sibling of the trigger button — so
 * action clicks never toggle the panel and the markup stays valid.
 */
function AccordionTrigger({
  className,
  children,
  actions,
  ...props
}: AccordionPrimitive.Trigger.Props & { actions?: ReactNode }) {
  return (
    <AccordionPrimitive.Header className="flex items-center gap-2">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          "focus-visible:ring-ring/50 rounded-md py-4 text-left text-sm font-semibold focus-visible:ring-3 group/accordion-trigger flex flex-1 cursor-pointer items-center gap-2 border border-transparent transition-all outline-none aria-disabled:pointer-events-none aria-disabled:opacity-50",
          className,
        )}
        {...props}
      >
        <ChevronDownIcon
          data-slot="accordion-trigger-icon"
          className="text-muted-foreground pointer-events-none size-4 shrink-0 transition-transform group-aria-expanded/accordion-trigger:rotate-180"
        />
        {children}
      </AccordionPrimitive.Trigger>
      {actions != null && <div className="flex shrink-0 items-center">{actions}</div>}
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({ className, children, ...props }: AccordionPrimitive.Panel.Props) {
  return (
    <AccordionPrimitive.Panel
      data-slot="accordion-content"
      className="h-[var(--accordion-panel-height)] overflow-x-visible overflow-y-clip text-sm transition-all duration-200 ease-out data-[starting-style]:h-0 data-[ending-style]:h-0"
      {...props}
    >
      <div
        className={cn(
          "pt-0 pb-4 [&_a]:hover:text-foreground [&_a]:underline [&_a]:underline-offset-3 [&_p:not(:last-child)]:mb-4",
          className,
        )}
      >
        {children}
      </div>
    </AccordionPrimitive.Panel>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
